/**
 * 받은 메일 본문 그리기 — 3중 방어의 둘째·셋째 층 (v2.205.0 · TSP 이식 가이드 §8)
 *
 *   ① 서버 정리(sanitize-html · backend lib/mail-read/sanitize.ts) — 이미 거쳐서 온다
 *   ② **화면 정리(DOMPurify)** — 서버를 믿되 한 번 더(화면 ≥ 서버 불변식)
 *   ③ **격리 틀(sandbox iframe + srcdoc)** — 스크립트 금지 · 부모 화면에 손 못 댐 · 바깥 연결 금지(CSP)
 *
 * ⭐⭐DOMPurify 는 **따로 만든 인스턴스**를 쓴다(가이드 §8-2 ⑴) — 기본 인스턴스에 다른 곳이 훅을 걸면
 *   메일 본문이 그 훅을 타면서 표·정렬이 망가진다. 이 파일의 훅도 이 인스턴스에만 걸린다.
 * ⭐⭐`ALLOWED_URI_REGEXP` 를 좁히지 않는다(가이드 §8-2 ⑵) — 좁히면 `width="35"` 같은 숫자 속성이 전부 빠져
 *   메일 표가 무너진다. 그림 주소는 훅에서 따로 **data:image 만** 남긴다.
 * ⛔이 HTML 을 격리 틀 밖(일반 화면의 innerHTML)에 넣지 말 것.
 *
 * 🖼 v2.210.0 (2026-09-28 · 사용자 결정 「항상 보이기 · 스팸만 가림」 — 웍스 서명의 로고·아이콘이 「외부 그림」으로 가려졌다)
 *   바깥 그림(http·https)은 **스팸메일함 밖이면 보인다** — 서버(images=1) · 화면 정리(external) · 틀 CSP 셋이 같이 연다.
 *   스크립트·글꼴·CSS 파일은 그대로 막힌다. 스팸메일함은 가린 채 [그림 보기]를 눌러야 연다.
 */

import createDOMPurify from 'dompurify';

type Purifier = ReturnType<typeof createDOMPurify>;
let purifier: Purifier | null = null;

const SAFE_IMG = /^data:image\/(png|jpe?g|gif|webp|bmp);base64,[a-z0-9+/=]+$/i;
/** 🖼 바깥 그림 — purifyMailHtml(…, { external: true }) 일 때만 (v2.210.0 · 사용자 결정 「항상 보이기 · 스팸만 가림」) */
const WEB_IMG = /^https?:\/\//i;
/** 훅은 인스턴스에 한 번만 건다 — 이번 정리에서 바깥 그림을 살릴지는 이 값으로 넘긴다(정리는 동기라 섞이지 않는다) */
let allowWebImages = false;

function getPurifier(): Purifier {
  if (purifier) return purifier;
  const p = createDOMPurify(window);
  p.addHook('afterSanitizeAttributes', (node) => {
    const el = node as Element;
    if (el.tagName === 'A') {
      // 링크는 늘 새 창 + opener 끊기(서버도 했지만 한 번 더)
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
    } else if (el.tagName === 'IMG') {
      // 그림은 서버가 만든 data: 그림만 — 바깥 주소는 여기서도 떼어 낸다(열어 본 사실이 보낸 사람에게 새지 않게)
      //   단 바깥 그림을 보이게 한 메일(스팸메일함 밖 · v2.210.0)은 http·https 주소도 남긴다
      const src = el.getAttribute('src') ?? '';
      if (src && !SAFE_IMG.test(src) && !(allowWebImages && WEB_IMG.test(src))) el.removeAttribute('src');
      el.removeAttribute('srcset');
    }
  });
  purifier = p;
  return p;
}

/** 서버가 정리해 보낸 본문을 화면에서 한 번 더 정리한다 · external = 바깥 그림(http·https)도 남긴다 */
export function purifyMailHtml(html: string, opts: { external?: boolean } = {}): string {
  const p = getPurifier();
  allowWebImages = !!opts.external;
  try {
    return p.sanitize(html, {
      FORBID_TAGS: [
        'style', 'script', 'iframe', 'frame', 'form', 'input', 'button', 'textarea', 'select',
        'object', 'embed', 'svg', 'math', 'link', 'meta', 'base', 'video', 'audio', 'source',
      ],
      FORBID_ATTR: ['srcset', 'action', 'formaction'],
      ADD_ATTR: ['target'],
      ALLOW_DATA_ATTR: false,
    }) as string;
  } finally {
    allowWebImages = false;
  }
}

/**
 * 격리 틀에 넣을 문서 — 문서 자체에 CSP 를 박아 **바깥으로 아무것도 못 불러오게** 한다
 * (그림은 data: 만 · 글꼴·CSS 파일·스크립트 전부 금지). 틀의 sandbox 가 스크립트를 한 번 더 막는다.
 * 🖼 external 이면 그림만 http·https 를 더 연다(v2.210.0) — 글꼴·CSS·스크립트는 그대로 금지 ·
 *   no-referrer 로 포털 주소가 그림 주인에게 가지 않게.
 */
export function buildMailSrcdoc(cleanHtml: string, opts: { external?: boolean } = {}): string {
  const imgSrc = opts.external ? 'data: http: https:' : 'data:';
  return (
    '<!doctype html><html><head><meta charset="utf-8">'
    + `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${imgSrc}; style-src 'unsafe-inline'">`
    + '<meta name="referrer" content="no-referrer">'
    + '<base target="_blank">'
    + '<style>'
    + "body{margin:0;padding:14px 16px;font-family:'맑은 고딕','Malgun Gothic','Apple SD Gothic Neo',sans-serif;"
    + 'font-size:14px;line-height:1.6;color:#1e293b;word-break:break-word;overflow-wrap:anywhere;}'
    + 'img{max-width:100%;height:auto;}table{max-width:100%;}pre{white-space:pre-wrap;}a{color:#2563eb;}'
    + '</style></head><body>'
    + cleanHtml
    + '</body></html>'
  );
}
